import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { buildReportPayload } from "../reports/reportUtils";
import API_URL from "../../config/ApiUrl";

export const getSalesOrderReport = createAsyncThunk(
  "salesReport/getSalesOrderReport",
  async (payload, { rejectWithValue }) => {
    try {
      const body = buildReportPayload(payload);
      const { data } = await axios.post(API_URL.SALES_REPORT.SALES_ORDERS, body);
      return data;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const getPendingSOReport = createAsyncThunk(
  "salesReport/getPendingSOReport",
  async (payload, { rejectWithValue }) => {
    try {
      const body = buildReportPayload(payload);
      const { data } = await axios.post(API_URL.SALES_REPORT.PENDING_SOS, body);
      return data;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const getBackorderReport = createAsyncThunk(
  "salesReport/getBackorderReport",
  async (payload, { rejectWithValue }) => {
    try {
      const body = buildReportPayload(payload);
      const { data } = await axios.post(API_URL.SALES_REPORT.BACKORDERS, body);
      return data;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const getSalesByCustomerReport = createAsyncThunk(
  "salesReport/getSalesByCustomerReport",
  async (params, { rejectWithValue }) => {
    try {
      const query = new URLSearchParams();
      query.append("FromDate", params.fromDate);
      query.append("ToDate", params.toDate);
      if (params.customerIds?.length > 0) params.customerIds.forEach((id) => query.append("CustomerIds", id));
      if (params.walkInSearchTerms?.length > 0) params.walkInSearchTerms.forEach((t) => query.append("WalkInSearchTerms", t));
      if (params.salesPersonIds?.length > 0) params.salesPersonIds.forEach((id) => query.append("SalesPersonIds", id));
      if (params.locationIds?.length > 0) params.locationIds.forEach((id) => query.append("LocationIds", id));
      if (params.warehouseIds?.length > 0) params.warehouseIds.forEach((id) => query.append("WarehouseIds", id));
      if (params.customerCategoryIds?.length > 0) params.customerCategoryIds.forEach((id) => query.append("CustomerCategoryIds", id));
      if (params.customerAccountTypeIds?.length > 0) params.customerAccountTypeIds.forEach((id) => query.append("CustomerAccountTypeIds", id));
      if (params.sortBy) query.append("SortBy", params.sortBy);
      if (params.sortDirection) query.append("SortDirection", params.sortDirection);
      query.append("PageNumber", String(params.pageNumber || 1));
      query.append("PageSize", String(params.pageSize || 50));
      const { data } = await axios.get(`${API_URL.SALES_REPORT.SALES_BY_CUSTOMER}?${query.toString()}`);
      return data;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const getSalesBySalesPersonReport = createAsyncThunk(
  "salesReport/getSalesBySalesPersonReport",
  async (payload, { rejectWithValue }) => {
    try {
      const body = buildReportPayload(payload);
      const { data } = await axios.post(
        API_URL.SALES_REPORT.SALES_BY_SALES_PERSON,
        body,
      );
      return data;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const getSalesByItemReport = createAsyncThunk(
  "salesReport/getSalesByItemReport",
  async (params, { rejectWithValue }) => {
    try {
      const query = new URLSearchParams();
      query.append("FromDate", params.fromDate);
      query.append("ToDate", params.toDate);
      if (params.locationIds?.length > 0) params.locationIds.forEach((id) => query.append("LocationIds", id));
      if (params.warehouseIds?.length > 0) params.warehouseIds.forEach((id) => query.append("WarehouseIds", id));
      if (params.itemIds?.length > 0) params.itemIds.forEach((id) => query.append("ItemIds", id));
      if (params.categoryIds?.length > 0) params.categoryIds.forEach((id) => query.append("CategoryIds", id));
      if (params.brandIds?.length > 0) params.brandIds.forEach((id) => query.append("BrandIds", id));
      if (params.searchText) query.append("SearchText", params.searchText);
      if (params.sortBy) query.append("SortBy", params.sortBy);
      if (params.sortDirection) query.append("SortDirection", params.sortDirection);
      query.append("PageNumber", String(params.pageNumber || 1));
      query.append("PageSize", String(params.pageSize || 50));
      const { data } = await axios.get(`${API_URL.SALES_REPORT.SALES_BY_ITEM}?${query.toString()}`);
      return data;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const getSalesByCategoryReport = createAsyncThunk(
  "salesReport/getSalesByCategoryReport",
  async (params, { rejectWithValue }) => {
    try {
      const query = new URLSearchParams();
      query.append("FromDate", params.fromDate);
      query.append("ToDate", params.toDate);
      if (params.locationIds?.length > 0) params.locationIds.forEach((id) => query.append("LocationIds", id));
      if (params.warehouseIds?.length > 0) params.warehouseIds.forEach((id) => query.append("WarehouseIds", id));
      if (params.categoryIds?.length > 0) params.categoryIds.forEach((id) => query.append("CategoryIds", id));
      if (params.brandIds?.length > 0) params.brandIds.forEach((id) => query.append("BrandIds", id));
      if (params.searchText) query.append("SearchText", params.searchText);
      if (params.includeCounterSales) query.append("IncludeCounterSales", params.includeCounterSales);
      if (params.sortBy) query.append("Paging.OrderColumn", params.sortBy);
      if (params.sortDirection) query.append("Paging.OrderBy", params.sortDirection);
      query.append("Paging.PageNumber", String(params.pageNumber || 1));
      query.append("Paging.PageSize", String(params.pageSize || 50));
      const { data } = await axios.get(`${API_URL.SALES_REPORT.SALES_BY_CATEGORY}?${query.toString()}`);
      return data;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const getSalesByBrandReport = createAsyncThunk(
  "salesReport/getSalesByBrandReport",
  async (params, { rejectWithValue }) => {
    try {
      const query = new URLSearchParams();
      query.append("FromDate", params.fromDate);
      query.append("ToDate", params.toDate);
      if (params.locationIds?.length > 0) params.locationIds.forEach((id) => query.append("LocationIds", id));
      if (params.warehouseIds?.length > 0) params.warehouseIds.forEach((id) => query.append("WarehouseIds", id));
      if (params.categoryIds?.length > 0) params.categoryIds.forEach((id) => query.append("CategoryIds", id));
      if (params.brandIds?.length > 0) params.brandIds.forEach((id) => query.append("BrandIds", id));
      if (params.searchText) query.append("SearchText", params.searchText);
      if (params.includeCounterSales) query.append("IncludeCounterSales", params.includeCounterSales);
      if (params.sortBy) query.append("Paging.OrderColumn", params.sortBy);
      if (params.sortDirection) query.append("Paging.OrderBy", params.sortDirection);
      query.append("Paging.PageNumber", String(params.pageNumber || 1));
      query.append("Paging.PageSize", String(params.pageSize || 50));
      const { data } = await axios.get(`${API_URL.SALES_REPORT.SALES_BY_BRAND}?${query.toString()}`);
      return data;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const getSalesByInvoiceReport = createAsyncThunk(
  "salesReport/getSalesByInvoiceReport",
  async (params, { rejectWithValue }) => {
    try {
      const query = new URLSearchParams();
      query.append("FromDate", params.fromDate);
      query.append("ToDate", params.toDate);
      if (params.invoiceNumber) query.append("InvoiceNumber", params.invoiceNumber);
      if (params.customerIds?.length > 0) params.customerIds.forEach((id) => query.append("CustomerIds", id));
      if (params.walkInSearchTerms?.length > 0) params.walkInSearchTerms.forEach((t) => query.append("WalkInSearchTerms", t));
      if (params.locationIds?.length > 0) params.locationIds.forEach((id) => query.append("LocationIds", id));
      if (params.warehouseIds?.length > 0) params.warehouseIds.forEach((id) => query.append("WarehouseIds", id));
      if (params.stateIds?.length > 0) params.stateIds.forEach((id) => query.append("StateIds", id));
      if (params.city) query.append("City", params.city);
      if (params.customerAccountTypeIds?.length > 0) params.customerAccountTypeIds.forEach((id) => query.append("CustomerAccountTypeIds", id));
      if (params.customerCategoryIds?.length > 0) params.customerCategoryIds.forEach((id) => query.append("CustomerCategoryIds", id));
      if (params.sourceFilter) query.append("SourceFilter", params.sourceFilter);
      if (params.sortBy) query.append("SortBy", params.sortBy);
      if (params.sortDirection) query.append("SortDirection", params.sortDirection);
      query.append("PageNumber", String(params.pageNumber || 1));
      query.append("PageSize", String(params.pageSize || 50));
      const { data } = await axios.get(`${API_URL.SALES_REPORT.SALES_BY_INVOICE}?${query.toString()}`);
      return data;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const getCustomerItemSalesReport = createAsyncThunk(
  "salesReport/getCustomerItemSalesReport",
  async (payload, { rejectWithValue }) => {
    try {
      const body = buildReportPayload(payload);
      const { data } = await axios.post(
        API_URL.SALES_REPORT.CUSTOMER_ITEM_SALES,
        body,
      );
      return data;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const getCustomerMonthlySalesReport = createAsyncThunk(
  "salesReport/getCustomerMonthlySalesReport",
  async (params, { rejectWithValue }) => {
    try {
      const query = new URLSearchParams();
      query.append("FromDate", params.fromDate);
      query.append("ToDate", params.toDate);
      if (params.customerIds?.length > 0) params.customerIds.forEach((id) => query.append("CustomerIds", id));
      if (params.locationIds?.length > 0) params.locationIds.forEach((id) => query.append("LocationIds", id));
      if (params.warehouseIds?.length > 0) params.warehouseIds.forEach((id) => query.append("WarehouseIds", id));
      if (params.salesPersonIds?.length > 0) params.salesPersonIds.forEach((id) => query.append("SalesPersonIds", id));
      if (params.itemBrandIds?.length > 0) params.itemBrandIds.forEach((id) => query.append("ItemBrandIds", id));
      if (params.itemCategoryIds?.length > 0) params.itemCategoryIds.forEach((id) => query.append("ItemCategoryIds", id));
      if (params.customerCategoryIds?.length > 0) params.customerCategoryIds.forEach((id) => query.append("CustomerCategoryIds", id));
      if (params.customerAccountTypeIds?.length > 0) params.customerAccountTypeIds.forEach((id) => query.append("CustomerAccountTypeIds", id));
      if (params.sortBy) query.append("SortBy", params.sortBy);
      if (params.sortDirection) query.append("SortDirection", params.sortDirection);
      query.append("PageNumber", String(params.pageNumber || 1));
      query.append("PageSize", String(params.pageSize || 50));
      const { data } = await axios.get(`${API_URL.SALES_REPORT.CUSTOMER_MONTHLY_SALES}?${query.toString()}`);
      return data;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const getNetSalesRegisterReport = createAsyncThunk(
  "salesReport/getNetSalesRegisterReport",
  async (params, { rejectWithValue }) => {
    try {
      const query = new URLSearchParams();
      query.append("FromDate", params.fromDate);
      query.append("ToDate", params.toDate);
      if (params.customerIds?.length > 0) params.customerIds.forEach((id) => query.append("CustomerIds", id));
      if (params.walkInSearchTerms?.length > 0) params.walkInSearchTerms.forEach((t) => query.append("WalkInSearchTerms", t));
      if (params.invoiceTypes?.length > 0) params.invoiceTypes.forEach((type) => query.append("InvoiceTypes", type));
      if (params.searchText) query.append("SearchText", params.searchText);
      if (params.locationIds?.length > 0) params.locationIds.forEach((id) => query.append("LocationIds", id));
      if (params.warehouseIds?.length > 0) params.warehouseIds.forEach((id) => query.append("WarehouseIds", id));
      if (params.salesPersonIds?.length > 0) params.salesPersonIds.forEach((id) => query.append("SalesPersonIds", id));
      if (params.itemBrandIds?.length > 0) params.itemBrandIds.forEach((id) => query.append("ItemBrandIds", id));
      if (params.itemCategoryIds?.length > 0) params.itemCategoryIds.forEach((id) => query.append("ItemCategoryIds", id));
      if (params.customerCategoryIds?.length > 0) params.customerCategoryIds.forEach((id) => query.append("CustomerCategoryIds", id));
      if (params.customerAccountTypeIds?.length > 0) params.customerAccountTypeIds.forEach((id) => query.append("CustomerAccountTypeIds", id));
      if (params.sourceFilter) query.append("SourceFilter", params.sourceFilter);
      if (params.sortColumn) query.append("SortColumn", params.sortColumn);
      if (params.sortDirection) query.append("SortDirection", params.sortDirection);
      query.append("PageNumber", String(params.pageNumber || 1));
      query.append("PageSize", String(params.pageSize || 50));
      const { data } = await axios.get(`${API_URL.SALES_REPORT.NET_SALES_REGISTER}?${query.toString()}`);
      return data;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const getBusinessSalesTransactionReport = createAsyncThunk(
  "salesReport/getBusinessSalesTransactionReport",
  async (params, { rejectWithValue }) => {
    try {
      const query = new URLSearchParams();
      query.append("FromDate", params.fromDate);
      query.append("ToDate", params.toDate);
      if (params.customerIds?.length > 0) params.customerIds.forEach((id) => query.append("CustomerIds", id));
      if (params.walkInSearchTerms?.length > 0) params.walkInSearchTerms.forEach((t) => query.append("WalkInSearchTerms", t));
      if (params.invoiceTypes?.length > 0) params.invoiceTypes.forEach((type) => query.append("InvoiceTypes", type));
      if (params.searchText) query.append("SearchText", params.searchText);
      if (params.locationIds?.length > 0) params.locationIds.forEach((id) => query.append("LocationIds", id));
      if (params.warehouseIds?.length > 0) params.warehouseIds.forEach((id) => query.append("WarehouseIds", id));
      if (params.salesPersonIds?.length > 0) params.salesPersonIds.forEach((id) => query.append("SalesPersonIds", id));
      if (params.itemBrandIds?.length > 0) params.itemBrandIds.forEach((id) => query.append("ItemBrandIds", id));
      if (params.itemCategoryIds?.length > 0) params.itemCategoryIds.forEach((id) => query.append("ItemCategoryIds", id));
      if (params.itemIds?.length > 0) params.itemIds.forEach((id) => query.append("ItemIds", id));
      if (params.customerCategoryIds?.length > 0) params.customerCategoryIds.forEach((id) => query.append("CustomerCategoryIds", id));
      if (params.customerAccountTypeIds?.length > 0) params.customerAccountTypeIds.forEach((id) => query.append("CustomerAccountTypeIds", id));
      if (params.sourceFilter) query.append("SourceFilter", params.sourceFilter);
      if (params.sortColumn) query.append("SortColumn", params.sortColumn);
      if (params.sortDirection) query.append("SortDirection", params.sortDirection);
      query.append("PageNumber", String(params.pageNumber || 1));
      query.append("PageSize", String(params.pageSize || 50));
      const { data } = await axios.get(`${API_URL.SALES_REPORT.BUSINESS_SALES_TRANSACTION}?${query.toString()}`);
      return data;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const getCounterSalesReport = createAsyncThunk(
  "salesReport/getCounterSalesReport",
  async (params, { rejectWithValue }) => {
    try {
      const query = new URLSearchParams();
      query.append("FromDate", params.fromDate);
      query.append("ToDate", params.toDate);
      if (params.locationIds?.length > 0) params.locationIds.forEach((id) => query.append("LocationIds", id));
      if (params.warehouseIds?.length > 0) params.warehouseIds.forEach((id) => query.append("WarehouseIds", id));
      if (params.customerIds?.length > 0) params.customerIds.forEach((id) => query.append("CustomerIds", id));
      if (params.walkInSearchTerms?.length > 0) params.walkInSearchTerms.forEach((term) => query.append("WalkInSearchTerms", term));
      if (params.itemIds?.length > 0) params.itemIds.forEach((id) => query.append("ItemIds", id));
      if (params.categoryIds?.length > 0) params.categoryIds.forEach((id) => query.append("CategoryIds", id));
      if (params.brandIds?.length > 0) params.brandIds.forEach((id) => query.append("BrandIds", id));
      if (params.searchText) query.append("SearchText", params.searchText);
      if (params.sortBy) query.append("SortBy", params.sortBy);
      if (params.sortDirection) query.append("SortDirection", params.sortDirection);
      query.append("PageNumber", String(params.pageNumber || 1));
      query.append("PageSize", String(params.pageSize || 50));
      const { data } = await axios.get(`${API_URL.SALES_REPORT.COUNTER_SALES_REPORT}?${query.toString()}`);
      return data;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

// Get Counter Sale Detail (for reports - summary data with items)
export const getCounterSaleDetail = createAsyncThunk(
  "salesReport/getCounterSaleDetail",
  async (counterSaleId, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      params.append("CounterSaleId", counterSaleId);
      const { data } = await axios.get(
        `${API_URL.SALES_REPORT.COUNTER_SALES_DETAIL}?${params.toString()}`,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const getSalesVsFulfillmentReport = createAsyncThunk(
  "salesReport/getSalesVsFulfillmentReport",
  async (payload, { rejectWithValue }) => {
    try {
      const body = buildReportPayload(payload);
      const { data } = await axios.post(API_URL.SALES_REPORT.SALES_VS_FULFILLMENT_REPORT, body);
      return data;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const getFulfillmentDetails = createAsyncThunk(
  "salesReport/getFulfillmentDetails",
  async (salesOrderId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.SALES_REPORT.SALES_VS_FULFILLMENT_DETAILS(salesOrderId));
      return data;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  }
);

export const getSalesReturnAndRMAReport = createAsyncThunk(
  "salesReport/getSalesReturnAndRMAReport",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(API_URL.SALES_REPORT.SALES_RETURN_AND_RMA, payload);
      return data;
    } catch (error) {
      return rejectWithValue(error?.data || error?.response?.data);
    }
  }
);

const initialState = {
  salesOrders: { list: [], total_records: 0 },
  pendingSOs: { list: [], total_records: 0 },
  backorders: { list: [], total_records: 0, totals: null },
  salesByCustomer: { items: [], summary: {}, total_records: 0, loading: false },
  salesBySalesPerson: { items: [], summary: {}, total_records: 0, loading: false },
  salesByItem: { items: [], summary: {}, total_records: 0, loading: false },
  salesByCategory: { list: [], totals: {}, total_records: 0, loading: false },
  salesByBrand: { list: [], totals: {}, total_records: 0, loading: false },
  salesByInvoice: { items: [], summary: {}, total_records: 0, loading: false },
  customerItemSales: { list: [], total_records: 0 },
  customerMonthlySales: { items: [], summary: {}, total_records: 0, loading: false },
  netSalesRegister: { items: [], summary: {}, total_records: 0, loading: false },
  businessSalesTransactionReport: { items: [], summary: {}, total_records: 0, loading: false },
  counterSalesReport: { items: [], summary: {}, total_records: 0, loading: false },
  salesVsFulfillment: { list: [], total_records: 0, totals: null, loading: false, detailLoading: false },
  salesReturnAndRMA: { items: [], summary: {}, total_records: 0, loading: false },
  fulfillmentDetail: null,
  counterSaleDetail: null,
  loading: false,
  error: null,
};

const salesReportSlice = createSlice({
  name: "salesReport",
  initialState,
  // Per-report reset actions so each report page can wipe its slice slot on
  // unmount. Prevents stale rows from re-appearing when the user navigates
  // away and back (the report should not auto-fetch on mount — see each page's
  // "No auto-fetch on mount — user must click Run Report" comment).
  reducers: {
    resetSalesByCustomerReport: (state) => {
      state.salesByCustomer = initialState.salesByCustomer;
    },
    resetSalesBySalesPersonReport: (state) => {
      state.salesBySalesPerson = initialState.salesBySalesPerson;
    },
    resetSalesByItemReport: (state) => {
      state.salesByItem = initialState.salesByItem;
    },
    resetSalesByCategoryReport: (state) => {
      state.salesByCategory = initialState.salesByCategory;
    },
    resetSalesByBrandReport: (state) => {
      state.salesByBrand = initialState.salesByBrand;
    },
    resetSalesByInvoiceReport: (state) => {
      state.salesByInvoice = initialState.salesByInvoice;
    },
    resetCustomerItemSalesReport: (state) => {
      state.customerItemSales = initialState.customerItemSales;
    },
    resetCustomerMonthlySalesReport: (state) => {
      state.customerMonthlySales = initialState.customerMonthlySales;
    },
    resetNetSalesRegisterReport: (state) => {
      state.netSalesRegister = initialState.netSalesRegister;
    },
    resetBusinessSalesTransactionReport: (state) => {
      state.businessSalesTransactionReport = initialState.businessSalesTransactionReport;
    },
    resetCounterSalesReport: (state) => {
      state.counterSalesReport = initialState.counterSalesReport;
    },
    clearCounterSaleDetail: (state) => {
      state.counterSaleDetail = null;
    },
    resetSalesVsFulfillmentReport: (state) => {
      state.salesVsFulfillment = initialState.salesVsFulfillment;
    },
    clearFulfillmentDetail: (state) => {
      state.fulfillmentDetail = null;
    },
    resetSalesReturnAndRMAReport: (state) => {
      state.salesReturnAndRMA = initialState.salesReturnAndRMA;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSalesOrderReport.fulfilled, (state, action) => {
        state.salesOrders.list = action.payload?.data?.list || [];
        state.salesOrders.total_records = action.payload?.data?.totalRecords || 0;
      })
      .addCase(getPendingSOReport.fulfilled, (state, action) => {
        state.pendingSOs.list = action.payload?.data?.list || [];
        state.pendingSOs.total_records = action.payload?.data?.totalRecords || 0;
      })
      .addCase(getBackorderReport.fulfilled, (state, action) => {
        state.backorders.list = action.payload?.data?.list || [];
        state.backorders.total_records = action.payload?.data?.totalRecords || 0;
        state.backorders.totals = action.payload?.data?.totals || null;
      })
      .addCase(getSalesByCustomerReport.pending, (state) => {
        state.salesByCustomer.loading = true;
      })
      .addCase(getSalesByCustomerReport.fulfilled, (state, action) => {
        state.salesByCustomer.items = action.payload?.data?.items || [];
        state.salesByCustomer.summary = action.payload?.data?.summary || {};
        state.salesByCustomer.total_records = action.payload?.data?.totalRecords || 0;
        state.salesByCustomer.loading = false;
      })
      .addCase(getSalesByCustomerReport.rejected, (state) => {
        state.salesByCustomer.loading = false;
      })
      .addCase(getSalesBySalesPersonReport.pending, (state) => {
        state.salesBySalesPerson.loading = true;
      })
      .addCase(getSalesBySalesPersonReport.fulfilled, (state, action) => {
        state.salesBySalesPerson.items = action.payload?.data?.items || [];
        state.salesBySalesPerson.summary = action.payload?.data?.summary || {};
        state.salesBySalesPerson.total_records = action.payload?.data?.totalRecords || 0;
        state.salesBySalesPerson.loading = false;
      })
      .addCase(getSalesBySalesPersonReport.rejected, (state) => {
        state.salesBySalesPerson.loading = false;
      })
      .addCase(getSalesByItemReport.pending, (state) => {
        state.salesByItem.loading = true;
      })
      .addCase(getSalesByItemReport.fulfilled, (state, action) => {
        state.salesByItem.items = action.payload?.data?.items || [];
        state.salesByItem.summary = action.payload?.data?.summary || {};
        state.salesByItem.total_records = action.payload?.data?.totalRecords || 0;
        state.salesByItem.loading = false;
      })
      .addCase(getSalesByItemReport.rejected, (state) => {
        state.salesByItem.loading = false;
      })
      .addCase(getSalesByCategoryReport.pending, (state) => {
        state.salesByCategory.loading = true;
      })
      .addCase(getSalesByCategoryReport.fulfilled, (state, action) => {
        state.salesByCategory.list = action.payload?.data?.list || [];
        state.salesByCategory.totals = action.payload?.data?.totals || {};
        state.salesByCategory.total_records = action.payload?.data?.totalRecords || 0;
        state.salesByCategory.loading = false;
      })
      .addCase(getSalesByCategoryReport.rejected, (state) => {
        state.salesByCategory.loading = false;
      })
      .addCase(getSalesByBrandReport.pending, (state) => {
        state.salesByBrand.loading = true;
      })
      .addCase(getSalesByBrandReport.fulfilled, (state, action) => {
        state.salesByBrand.list = action.payload?.data?.list || [];
        state.salesByBrand.totals = action.payload?.data?.totals || {};
        state.salesByBrand.total_records = action.payload?.data?.totalRecords || 0;
        state.salesByBrand.loading = false;
      })
      .addCase(getSalesByBrandReport.rejected, (state) => {
        state.salesByBrand.loading = false;
      })
      .addCase(getSalesByInvoiceReport.pending, (state) => {
        state.salesByInvoice.loading = true;
      })
      .addCase(getSalesByInvoiceReport.fulfilled, (state, action) => {
        state.salesByInvoice.items = action.payload?.data?.items || [];
        state.salesByInvoice.summary = action.payload?.data?.summary || {};
        state.salesByInvoice.total_records = action.payload?.data?.totalRecords || 0;
        state.salesByInvoice.loading = false;
      })
      .addCase(getSalesByInvoiceReport.rejected, (state) => {
        state.salesByInvoice.loading = false;
      })
      .addCase(getCustomerItemSalesReport.fulfilled, (state, action) => {
        state.customerItemSales.list = action.payload?.data?.list || [];
        state.customerItemSales.total_records =
          action.payload?.data?.totalRecords || 0;
      })
      .addCase(getCustomerMonthlySalesReport.pending, (state) => {
        state.customerMonthlySales.loading = true;
      })
      .addCase(getCustomerMonthlySalesReport.fulfilled, (state, action) => {
        state.customerMonthlySales.items = action.payload?.data?.items || [];
        state.customerMonthlySales.summary = action.payload?.data?.summary || {};
        state.customerMonthlySales.total_records = action.payload?.data?.totalRecords || 0;
        state.customerMonthlySales.loading = false;
      })
      .addCase(getCustomerMonthlySalesReport.rejected, (state) => {
        state.customerMonthlySales.loading = false;
      })
      .addCase(getNetSalesRegisterReport.pending, (state) => {
        state.netSalesRegister.loading = true;
      })
      .addCase(getNetSalesRegisterReport.fulfilled, (state, action) => {
        // Handle different response structures
        const responseData = action.payload?.data || action.payload;
        state.netSalesRegister.items = responseData?.netSalesRegisters || [];
        state.netSalesRegister.summary = responseData?.summary || {};
        state.netSalesRegister.total_records = responseData?.totalRecords || 0;
        state.netSalesRegister.loading = false;
      })
      .addCase(getNetSalesRegisterReport.rejected, (state) => {
        state.netSalesRegister.loading = false;
      })
      .addCase(getBusinessSalesTransactionReport.pending, (state) => {
        state.businessSalesTransactionReport.loading = true;
      })
      .addCase(getBusinessSalesTransactionReport.fulfilled, (state, action) => {
        const responseData = action.payload?.data || action.payload;
        state.businessSalesTransactionReport.items = responseData?.data || [];
        state.businessSalesTransactionReport.summary = responseData?.summary || {};
        state.businessSalesTransactionReport.total_records = responseData?.totalRecords || 0;
        state.businessSalesTransactionReport.loading = false;
      })
      .addCase(getBusinessSalesTransactionReport.rejected, (state) => {
        state.businessSalesTransactionReport.loading = false;
      })
      .addCase(getCounterSalesReport.pending, (state) => {
        state.counterSalesReport.loading = true;
      })
      .addCase(getCounterSalesReport.fulfilled, (state, action) => {
        state.counterSalesReport.items = action.payload?.data?.items || [];
        state.counterSalesReport.summary = action.payload?.data?.summary || {};
        state.counterSalesReport.total_records = action.payload?.data?.totalRecords || 0;
        state.counterSalesReport.loading = false;
      })
      .addCase(getCounterSalesReport.rejected, (state) => {
        state.counterSalesReport.loading = false;
      })
      .addCase(getCounterSaleDetail.pending, (state) => {
        state.counterSaleDetail = null;
      })
      .addCase(getCounterSaleDetail.fulfilled, (state, action) => {
        state.counterSaleDetail = action.payload;
      })
      .addCase(getCounterSaleDetail.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(getSalesVsFulfillmentReport.pending, (state) => {
        state.salesVsFulfillment.loading = true;
      })
      .addCase(getSalesVsFulfillmentReport.fulfilled, (state, action) => {
        state.salesVsFulfillment.list = action.payload?.data?.list || [];
        state.salesVsFulfillment.total_records = action.payload?.data?.totalRecords || 0;
        state.salesVsFulfillment.totals = action.payload?.data?.totals || null;
        state.salesVsFulfillment.loading = false;
      })
      .addCase(getSalesVsFulfillmentReport.rejected, (state) => {
        state.salesVsFulfillment.loading = false;
      })
      .addCase(getFulfillmentDetails.pending, (state) => {
        state.fulfillmentDetail = null;
        state.salesVsFulfillment.detailLoading = true;
      })
      .addCase(getFulfillmentDetails.fulfilled, (state, action) => {
        state.fulfillmentDetail = action.payload?.data;
        state.salesVsFulfillment.detailLoading = false;
      })
      .addCase(getFulfillmentDetails.rejected, (state, action) => {
        state.error = action.payload;
        state.salesVsFulfillment.detailLoading = false;
      })
      .addCase(getSalesReturnAndRMAReport.pending, (state) => {
        state.salesReturnAndRMA.loading = true;
      })
      .addCase(getSalesReturnAndRMAReport.fulfilled, (state, action) => {
        const responseData = action.payload?.data || action.payload;
        state.salesReturnAndRMA.items = responseData?.items || [];
        state.salesReturnAndRMA.summary = responseData?.summary || {};
        state.salesReturnAndRMA.total_records = responseData?.totalRecords || 0;
        state.salesReturnAndRMA.loading = false;
      })
      .addCase(getSalesReturnAndRMAReport.rejected, (state) => {
        state.salesReturnAndRMA.loading = false;
      });
  },
});

export const {
  resetSalesByCustomerReport,
  resetSalesBySalesPersonReport,
  resetSalesByItemReport,
  resetSalesByCategoryReport,
  resetSalesByBrandReport,
  resetSalesByInvoiceReport,
  resetCustomerItemSalesReport,
  resetCustomerMonthlySalesReport,
  resetNetSalesRegisterReport,
  resetBusinessSalesTransactionReport,
  resetCounterSalesReport,
  clearCounterSaleDetail,
  resetSalesVsFulfillmentReport,
  clearFulfillmentDetail,
  resetSalesReturnAndRMAReport,
} = salesReportSlice.actions;

export default salesReportSlice.reducer;
